/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { SETUP_PAGE_URL } from '@/api/core/links';
import { type FC, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Route, RouterProvider, Routes } from 'react-router-dom';
import { App } from '../App';
import { ContactType } from '../models/Contact';
import { AuthWrapper } from '../modules/auth/AuthWrapper';
import { useAuth } from '../modules/auth/core/Auth';
import { Logout } from '../modules/auth/Logout';
import { ErrorsPage } from '../modules/errors/ErrorsPage';
import CustomerRegistrationConfirmationPage from '../pages/registrations/customer-registration/CustomerRegistrationConfirmationPage';
import AcceptanceInvitationPage from '../pages/registrations/invite/AcceptanceInvitationPage';
import SetupPage from '../pages/setup/SetupPage';
import AccountantRoutes from './AccountantRoutes';
import { ManagerRoutes } from './ManagerRoutes';
import { SystemUserRoutes } from './SystemUserRoutes';
import { getAuthenticatedLandingRoute } from './getAuthenticatedLandingRoute';
import { getUserTypeFromJWT } from '../modules/auth/core/_helpers';

const { BASE_URL } = import.meta.env;

const USER_TYPE_ROUTES: Partial<Record<ContactType, ReactNode>> = {
  [ContactType.COMMUNITY]: <ManagerRoutes />,
  [ContactType.ACCOUNTANT]: <AccountantRoutes />,
  [ContactType.ADMIN]: <SystemUserRoutes />,
  [ContactType.SYSTEM_USER]: <SystemUserRoutes />,
};

const renderUnauthenticatedRoutes = () => {
  return (
    <>
      <Route element={<AuthWrapper />} path="auth/*" />
      <Route element={<SetupPage />} path={SETUP_PAGE_URL} />
      <Route element={<Navigate replace to="/auth/login" />} path="*" />
    </>
  );
};

const renderAuthenticatedRoutes = (authToken: string) => {
  const userType = getUserTypeFromJWT(authToken) as ContactType;
  const RouteElement = USER_TYPE_ROUTES[userType] ?? <SystemUserRoutes />;
  const landingRoute = getAuthenticatedLandingRoute(userType);
  return (
    <>
      {/* Just logged in — URL is still /auth/login, so send the user to their type's home. */}
      <Route element={<Navigate replace to={landingRoute} />} path="auth/*" />
      <Route element={RouteElement} path="/*" />
    </>
  );
};

// Árvore declarativa existente, renderizada dentro do data router. Lê o auth
// via contexto para que trocas de sessão não recriem o router (e o histórico).
const RootRoutes: FC = () => {
  const { auth } = useAuth();

  return (
    <Routes>
      <Route element={<App />}>
        <Route element={<ErrorsPage />} path="error/*" />
        <Route element={<Logout />} path="auth/logout" />
        <Route element={<AcceptanceInvitationPage />} path="convite/:invitationId" />
        <Route element={<CustomerRegistrationConfirmationPage />} path="confirmar-cadastro/:token" />
        {auth ? renderAuthenticatedRoutes(auth.token) : renderUnauthenticatedRoutes()}
      </Route>
    </Routes>
  );
};

// Data router mínimo (um wildcard) — necessário para o useBlocker estável do
// React Router 7 (useNavigationBlocker); a árvore de rotas continua declarativa.
const router = createBrowserRouter([{ path: '*', element: <RootRoutes /> }], {
  basename: BASE_URL,
});

const AppRoutes: FC = () => <RouterProvider router={router} />;

export { AppRoutes, RootRoutes };
